import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Email as EmailIcon } from "@mui/icons-material";
import { TabProps } from "../../../SourceModal.types";
import { getSourceCategory, hasFeature } from "../../../SourceModal.config";
import { sendFormToService } from "helpers/trackers/createTracker";
import SwalAlertComp from "components/SwalAlert";
import SendInstructionsModal from "../../../SendInstructionsModal";
import {
    JsOverview,
    RedirectOverview,
    CapiOverview,
    AdjustOverview,
    AppsflyerOverview,
    CRMOverview,
    FileOverview,
    AppOverview,
} from "./overviews";
import LinkedTrackers from "components/organisms/LinkedTrackers";

const OverviewTab: React.FC<TabProps> = (props) => {
    const { source, tracker, language } = props;
    const [sendInstructionsOpen, setSendInstructionsOpen] = useState(false);

    const sourceCode = source?.source_type?.code;
    const category = getSourceCategory(sourceCode);
    const showSendInstructions = hasFeature(sourceCode, "sendInstructions" ) && tracker;

    const handleSendInstructions = async (payload: any) => {
        try {
            const result = await sendFormToService(payload);

            if (result.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: result.message,
                    icon: "success",
                });
                return result;
            } else {
                throw new Error(
                    result.message || "Failed to send instructions",
                );
            }
        } catch (error: any) {
            throw new Error(error.message || "Failed to send instructions");
        }
    };

    const renderOverviewContent = () => {
        switch (sourceCode) {
            case "js":
                return <JsOverview {...props} />;
            case "redirect":
                return <RedirectOverview {...props} />;
            case "capi":
                return <CapiOverview {...props} />;
            case "adjust":
                return <AdjustOverview {...props} />;
            case "appsflyer":
                return <AppsflyerOverview {...props} />;
            case "androidcapi":
            case "ioscapi":
                return <AppOverview {...props} />;
            default:
                switch (category) {
                    case "crm":
                        return <CRMOverview {...props} />;
                    case "file":
                        return <FileOverview {...props} />;
                    case "app":
                        return <AppOverview {...props} />;
                    default:
                        return <CRMOverview {...props} />;
                }
        }
    };

    return (
        <Box>
            {renderOverviewContent()}

            <LinkedTrackers
                trackers={source?.trackers}
                onClose={props.onClose}
            />

            {showSendInstructions && (
                <>
                    <Box
                        sx={{
                            backgroundColor: "grey.50",
                            p: 3,
                            borderRadius: 2,
                            mt: 3,
                            border: "1px solid",
                            borderColor: "grey.200",
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {language?.tracker_detail?.send_instructions ??
                                "Send Instructions"}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            paragraph
                        >
                            {language?.tracker_detail?.send_instruction_text ??
                                "Send implementation instructions to your developer with all necessary documentation and setup guides."}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<EmailIcon />}
                            onClick={() => setSendInstructionsOpen(true)}
                            sx={{ mt: 1 }}
                        >
                            {language?.buttons?.send_document ??
                                "Send Instructions"}
                        </Button>
                    </Box>

                    {tracker && (
                        <SendInstructionsModal
                            open={sendInstructionsOpen}
                            onClose={() => setSendInstructionsOpen(false)}
                            tracker={tracker}
                            language={language}
                            onSendInstructions={handleSendInstructions}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default OverviewTab;
